import React, { useEffect, useState, useCallback } from 'react';
import { sarAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { StatCard } from '../../components/import/ImportPrimitives';
import { ImportPicker } from './ImportPicker';
import { GitCompareArrows, Loader2, ShieldCheck, CheckCircle2, AlertTriangle } from 'lucide-react';

const PRESENCE = {
  both: { label: 'Both', cls: 'text-slate-700 bg-slate-100' },
  sar_only: { label: 'SAR only', cls: 'text-amber-700 bg-amber-50' },
  approved_only: { label: 'Approved only', cls: 'text-sky-700 bg-sky-50' },
};

export default function HoursComparison() {
  const [imports, setImports] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    sarAPI.listImports(true)
      .then(({ data }) => {
        const imps = data.imports || [];
        setImports(imps);
        const preferred = imps.find((i) => i.is_final) || imps[0];
        if (preferred) setSelectedId(preferred.id);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load imports'));
  }, []);

  const run = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await sarAPI.hoursComparison(id);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not compute hours comparison');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedId) run(selectedId); }, [selectedId, run]);

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="sar-hours-tab">
      <Alert className="border-sky-200 bg-sky-50" data-testid="sar-hours-readonly">
        <ShieldCheck className="h-4 w-4 text-sky-600" />
        <AlertDescription className="text-sky-800 text-sm">
          <strong>Hours only, read-only.</strong> Compares SAR paid hours (Track A) against approved,
          locked TriCorps timesheet hours (Track B) by employee and week. No pay or dollars are read.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><GitCompareArrows className="w-5 h-5" /> Hours comparison</CardTitle>
          <CardDescription>Choose an import to line up SAR paid hours against approved TriCorps hours, per employee and week.</CardDescription>
        </CardHeader>
        <CardContent>
          <ImportPicker imports={imports} value={selectedId} onChange={setSelectedId} testId="sar-hours-import-picker" />
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="sar-hours-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Comparing hours…
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Employee-weeks" value={String(s.week_count ?? '—')} testId="sar-hc-stat-weeks" />
            <StatCard label="Matches" value={String(s.match_count ?? '—')} tone="text-emerald-700" testId="sar-hc-stat-match" />
            <StatCard label="Differences" value={String(s.difference_count ?? '—')} tone="text-amber-700" testId="sar-hc-stat-diff" />
            <StatCard label="SAR only" value={String(s.sar_only_count ?? '—')} testId="sar-hc-stat-saronly" />
            <StatCard label="Approved only" value={String(s.approved_only_count ?? '—')} testId="sar-hc-stat-apponly" />
          </div>

          <Card data-testid="sar-hours-results">
            <CardHeader className="pb-2"><CardTitle className="text-sm">SAR vs approved hours by employee</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(data.weeks || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">Nothing to compare for this import.</p>
              ) : (
                <div className="overflow-auto max-h-[560px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Week</TableHead>
                        <TableHead className="text-right">SAR hrs</TableHead>
                        <TableHead className="text-right">Approved hrs</TableHead>
                        <TableHead className="text-right">Diff</TableHead>
                        <TableHead>Presence</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.weeks.map((w) => {
                        const p = PRESENCE[w.present_in] || PRESENCE.both;
                        const flagged = w.present_in !== 'both' || !w.matches;
                        return (
                          <TableRow key={`${w.employee_number}-${w.week_start}`} data-testid={`sar-hc-row-${w.employee_number}-${w.week_start}`} className={flagged ? 'bg-amber-50' : ''}>
                            <TableCell className="font-mono text-xs">{w.employee_number}{w.employee_name ? ` · ${w.employee_name}` : ''}</TableCell>
                            <TableCell className="text-xs">{w.week_start} → {w.week_end}</TableCell>
                            <TableCell className="text-right">{w.sar_hours ?? '—'}</TableCell>
                            <TableCell className="text-right">{w.approved_hours ?? '—'}</TableCell>
                            <TableCell className="text-right font-medium">{w.difference ?? '—'}</TableCell>
                            <TableCell><Badge variant="secondary" className={p.cls}>{p.label}</Badge></TableCell>
                            <TableCell>
                              {w.present_in === 'both' && w.matches ? (
                                <Badge variant="secondary" className="text-emerald-700 bg-emerald-50"><CheckCircle2 className="w-3 h-3 mr-1" />Match</Badge>
                              ) : (
                                <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Review</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
